import { getInstance } from "@/auth/auth0/index";
import { CustomerSettingsInterface } from "@/interfaces/customerSettings.interface";
import axios from "@/libs/axios";
import axiosV1 from "@/libs/axiosV1";
import { RequestQueryBuilder } from "@rewiko/crud-request";
import { useQuery } from "@tanstack/vue-query";
import { AxiosResponse } from "axios";
import lodash from "lodash";
import { defineStore } from "pinia";
import { BillingPeriodMonthsType } from "@/types/billingMonths.type";

interface State {
    isMigratingOldChannels: boolean;
    isLoadingCustomerSettings: boolean;
    isSavingCustomerSettings: boolean;
    customerSettings: CustomerSettingsInterface;
    editableCustomerSettings: Array<keyof CustomerSettingsInterface>;
}

// Can also be used for customer related things, like customer settings.
export const useCustomersStore = defineStore("customers", {
    state: (): State => ({
        isMigratingOldChannels: false,
        isLoadingCustomerSettings: false,
        isSavingCustomerSettings: false,
        customerSettings: {
            expandedTeamFilter: false,
            engageMessagesLimit: 0,
            engageUniqueContactsLimit: 0,
            apiEnabled: false,
            contactsModuleForAgentsEnabled: true,
            noticeEnabled: false,
            noticeText: "",
            customDataRetentionDays: 0,
            billingMonths: BillingPeriodMonthsType.YEARLY,
            customUserPriceYearly: -1,
            customUserPriceMonthly: -1,
            limitUsers: -1,
            openAiApiKey: null,
            openAiEnabled: false,
            embeddedSignupLimit: 0,
            birdEmbeddedSignupLimit: 0,
            flowsLimit: 0,
            chatwootLimit: 0,
        },
        editableCustomerSettings: ["expandedTeamFilter", "openAiApiKey"],
    }),
    actions: {
        fetchCustomerSettings(requestQueryBuilder?: RequestQueryBuilder): void {
            useQuery<Pick<AxiosResponse<CustomerSettingsInterface[]>, "data">>(
                ["customer-settings", requestQueryBuilder?.queryObject],
                () => {
                    this.isLoadingCustomerSettings = true;
                    return axios.get<CustomerSettingsInterface[]>(
                        `${process.env.VUE_APP_PUBLIC_API}/customer-settings`,
                        {
                            params: requestQueryBuilder?.queryObject,
                        }
                    );
                },
                {
                    staleTime: 300000, // 5min
                    placeholderData: { data: [] },
                    onSuccess: ({ data }) => {
                        if (Object.entries(data).length > 0) {
                            this.setCustomerSettings(data);
                        }
                    },
                    onSettled: () => (this.isLoadingCustomerSettings = false),
                }
            );
        },
        saveSettings(newSettings: Partial<CustomerSettingsInterface>): Promise<Partial<CustomerSettingsInterface>> {
            this.isSavingCustomerSettings = true;

            return axios
                .patch<Partial<CustomerSettingsInterface>>(
                    "customer-settings/from-map",
                    lodash.pick(newSettings, this.editableCustomerSettings)
                )
                .then(({ data }) => data)
                .finally(() => getInstance().reloadPrivileges())
                .finally(() => (this.isSavingCustomerSettings = false));
        },
        setCustomerSettings(newSettings: Partial<CustomerSettingsInterface>): void {
            this.customerSettings = lodash.assign(
                this.customerSettings,
                lodash.pick(newSettings, Object.keys(this.customerSettings))
            );
        },
        migrateMessageBirdChannels(): Promise<{ success: boolean }> {
            this.isMigratingOldChannels = true;
            return axiosV1
                .post(
                    `${(process.env.VUE_APP_API_GATEWAY ?? "").replace(
                        "v1",
                        "v2"
                    )}/channels/migrate-messagebird-channels`
                )
                .then(({ data }) => data)
                .finally(() => (this.isMigratingOldChannels = false));
        },
        syncWhatsappTemplates(): Promise<{ success: boolean }> {
            return axiosV1
                .post(`${(process.env.VUE_APP_API_GATEWAY ?? "").replace("v1", "v2")}/channels/sync-whatsapp-templates`)
                .then(({ data }) => data);
        },
    },
});
